import {
    GroupedHistogramData,
    GroupedTopLevelMetrics,
    OrganizationEngagement,
    TopLevelMetrics
} from '../../../../shared/api/controllers/engagementAnalyticsReporting';

export type PlatformHealthVariant = 'ordering' | 'scheduling';

export interface PlatformHealthApplication {
    id: string;
    name: string;
    isLive: boolean;
}

export interface PlatformHealthOrganization {
    id: string;
    name: string;
    matchingApps: PlatformHealthApplication[];
}

export interface PlatformHealthLocation {
    organizationId: string;
    organizationName: string;
    applicationId: string;
    applicationName: string;
}

export interface PlatformHealthMetricCounts {
    numerator: number;
    denominator: number;
}

export interface PlatformHealthRow extends PlatformHealthLocation, PlatformHealthMetricCounts {
    rate: number;
    threshold: number;
    deficit: number;
}

export interface PlatformHealthIgnoredRow extends PlatformHealthLocation, PlatformHealthMetricCounts {
    rate: number;
    threshold: number;
    targetDeficit: number;
}

export const DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS = 1;

const numberValue = (value: unknown): number => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};

const firstDefinedNumber = (...values: unknown[]): number => {
    const value = values.find(item => item !== undefined && item !== null);
    return numberValue(value);
};

const pairedMetric = (metrics: TopLevelMetrics, englishKey: string, spanishKey: string): number | undefined => {
    const english = metrics[englishKey];
    const spanish = metrics[spanishKey];
    return english == null && spanish == null ? undefined : numberValue(english) + numberValue(spanish);
};

const schedulingAttempts = (metrics: TopLevelMetrics): number => [
    'totalScheduleAttemptsEnglish',
    'totalScheduleAttemptsSpanish',
    'totalRescheduleAttemptsEnglish',
    'totalRescheduleAttemptsSpanish',
    'totalCancellationAttemptsEnglish',
    'totalCancellationAttemptsSpanish',
    'totalCheckNextAppointmentAttemptsEnglish',
    'totalCheckNextAppointmentAttemptsSpanish'
].reduce((total, key) => total + numberValue(metrics[key]), 0);

export const getPlatformHealthMetricCounts = (
    metrics: TopLevelMetrics = {},
    variant: PlatformHealthVariant
): PlatformHealthMetricCounts => {
    if (variant === 'ordering') {
        const orderCompletionEngagementsByLanguage = pairedMetric(
            metrics,
            'totalOrderCompletionEngagementsEnglish',
            'totalOrderCompletionEngagementsSpanish'
        );
        const ordersCompletedByLanguage = pairedMetric(
            metrics,
            'totalOrdersCompletedEnglish',
            'totalOrdersCompletedSpanish'
        );
        const orderAttemptsByLanguage = pairedMetric(
            metrics,
            'totalOrderAttemptsEnglish',
            'totalOrderAttemptsSpanish'
        );
        return {
            numerator: firstDefinedNumber(
                metrics.totalOrderCompletionEngagements,
                metrics.totalOrdersCompleted,
                orderCompletionEngagementsByLanguage,
                ordersCompletedByLanguage,
                metrics.ordersPlaced
            ),
            denominator: firstDefinedNumber(
                metrics.totalOrderAttempts,
                orderAttemptsByLanguage,
                metrics.orderAttempts,
                metrics.containableCalls
            )
        };
    }

    const containedByLanguage = pairedMetric(metrics, 'totalContainedCountEnglish', 'totalContainedCountSpanish');
    const containableByLanguage = pairedMetric(metrics, 'totalContainableCountEnglish', 'totalContainableCountSpanish');
    return {
        numerator: firstDefinedNumber(metrics.containedCalls, containedByLanguage, metrics.totalContainedCount),
        denominator: firstDefinedNumber(
            metrics.containableCalls,
            containableByLanguage,
            metrics.totalContainableCount,
            schedulingAttempts(metrics)
        )
    };
};

export const getPlatformHealthLocations = (
    organizations: PlatformHealthOrganization[]
): PlatformHealthLocation[] => organizations.reduce((locations, organization) => {
    organization.matchingApps
        .filter(application => application.isLive)
        .forEach(application => locations.push({
            organizationId: organization.id,
            organizationName: organization.name,
            applicationId: application.id,
            applicationName: application.name
        }));
    return locations;
}, [] as PlatformHealthLocation[]).sort((first, second) => (
    first.organizationName.localeCompare(second.organizationName) ||
    first.applicationName.localeCompare(second.applicationName)
));

const sumHistogramCounts = (entries: GroupedHistogramData[string] = []): number =>
    entries.reduce((total, entry) => total + numberValue(entry.count), 0);

export const buildPlatformHealthMetricsFromHistograms = (
    denominatorData: GroupedHistogramData = {},
    numeratorData: GroupedHistogramData = {},
    variant: PlatformHealthVariant
): GroupedTopLevelMetrics => {
    const applicationIds = new Set([
        ...Object.keys(denominatorData),
        ...Object.keys(numeratorData)
    ]);

    return Array.from(applicationIds).reduce((metricsByApplication, applicationId) => {
        const denominator = sumHistogramCounts(denominatorData[applicationId]);
        const numerator = sumHistogramCounts(numeratorData[applicationId]);
        metricsByApplication[applicationId] = variant === 'ordering'
            ? { totalOrderAttempts: denominator, totalOrderCompletionEngagements: numerator }
            : { containableCalls: denominator, containedCalls: numerator };
        return metricsByApplication;
    }, {} as GroupedTopLevelMetrics);
};

export const buildPlatformHealthMetricsFromEngagements = (
    engagements: OrganizationEngagement[] = [],
    variant: PlatformHealthVariant
): GroupedTopLevelMetrics => engagements.reduce((metricsByApplication, engagement) => {
    const applicationId = engagement.applicationId;
    if (!applicationId) return metricsByApplication;

    const tags = engagement.sessionTags || engagement.tags || [];
    if (variant === 'ordering') {
        if (engagement.sessionCategory !== 'ACTION_ATTEMPTED') return metricsByApplication;

        const metrics = metricsByApplication[applicationId] || {
            totalOrderAttempts: 0,
            totalOrderCompletionEngagements: 0
        };
        metrics.totalOrderAttempts += 1;
        if (tags.includes('ACTION_COMPLETED')) {
            metrics.totalOrderCompletionEngagements += 1;
        }
        metricsByApplication[applicationId] = metrics;
        return metricsByApplication;
    }

    // Match the Scheduling Analytics dashboard: outbound calls do not contribute
    // to containment, while contained and containable are counted independently.
    if (tags.includes('OUTBOUND_CALL') || (!engagement.isContainable && !engagement.isContained)) {
        return metricsByApplication;
    }

    const metrics = metricsByApplication[applicationId] || {
        containableCalls: 0,
        containedCalls: 0
    };
    if (engagement.isContainable) metrics.containableCalls += 1;
    if (engagement.isContained) metrics.containedCalls += 1;
    metricsByApplication[applicationId] = metrics;
    return metricsByApplication;
}, {} as GroupedTopLevelMetrics);

export const countPlatformHealthLocationsWithVolume = (
    organizations: PlatformHealthOrganization[],
    metricsByOrganization: Record<string, GroupedTopLevelMetrics>,
    selectedOrganizationIds: Set<string>,
    variant: PlatformHealthVariant,
    minimumAttempts: number = DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS
): number => getPlatformHealthLocations(organizations).filter(location => {
    if (!selectedOrganizationIds.has(location.organizationId)) return false;
    const metrics = metricsByOrganization[location.organizationId]?.[location.applicationId];
    return getPlatformHealthMetricCounts(metrics, variant).denominator >= Math.max(
        DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS,
        minimumAttempts
    );
}).length;

export const buildPlatformHealthRows = (
    organizations: PlatformHealthOrganization[],
    metricsByOrganization: Record<string, GroupedTopLevelMetrics>,
    selectedOrganizationIds: Set<string>,
    variant: PlatformHealthVariant,
    thresholds: Record<string, number>,
    defaultThreshold: number,
    minimumAttempts: number = DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS
): PlatformHealthRow[] => {
    const rows = getPlatformHealthLocations(organizations).reduce((result, location) => {
        if (!selectedOrganizationIds.has(location.organizationId)) {
            return result;
        }

        const metrics = metricsByOrganization[location.organizationId]?.[location.applicationId];
        const counts = getPlatformHealthMetricCounts(metrics, variant);
        if (counts.denominator < Math.max(DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS, minimumAttempts)) {
            return result;
        }

        const rate = counts.numerator / counts.denominator * 100;
        const threshold = thresholds[location.applicationId] == null
            ? defaultThreshold
            : thresholds[location.applicationId];
        if (rate >= threshold) {
            return result;
        }

        result.push({
            ...location,
            ...counts,
            rate,
            threshold,
            deficit: threshold - rate
        });
        return result;
    }, [] as PlatformHealthRow[]);

    return rows.sort((first, second) => (
        second.deficit - first.deficit ||
        first.applicationName.localeCompare(second.applicationName) ||
        first.organizationName.localeCompare(second.organizationName) ||
        first.applicationId.localeCompare(second.applicationId)
    ));
};

export const buildPlatformHealthIgnoredRows = (
    organizations: PlatformHealthOrganization[],
    metricsByOrganization: Record<string, GroupedTopLevelMetrics>,
    selectedOrganizationIds: Set<string>,
    variant: PlatformHealthVariant,
    thresholds: Record<string, number>,
    defaultThreshold: number,
    minimumAttempts: number = DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS
): PlatformHealthIgnoredRow[] => {
    const rows = getPlatformHealthLocations(organizations).reduce((result, location) => {
        if (!selectedOrganizationIds.has(location.organizationId)) return result;

        const threshold = thresholds[location.applicationId];
        if (threshold == null) return result;

        const metrics = metricsByOrganization[location.organizationId]?.[location.applicationId];
        const counts = getPlatformHealthMetricCounts(metrics, variant);
        if (counts.denominator < Math.max(DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS, minimumAttempts)) {
            return result;
        }

        const rate = counts.numerator / counts.denominator * 100;
        if (rate < threshold || rate >= defaultThreshold) return result;

        result.push({
            ...location,
            ...counts,
            rate,
            threshold,
            targetDeficit: defaultThreshold - rate
        });
        return result;
    }, [] as PlatformHealthIgnoredRow[]);

    return rows.sort((first, second) => (
        second.targetDeficit - first.targetDeficit ||
        first.applicationName.localeCompare(second.applicationName) ||
        first.applicationId.localeCompare(second.applicationId)
    ));
};
